import { 
  createSliceLayout,
  hasSliceContent,
  SliceBannerTout,
  SliceBannerToutProps,
  SliceColorStrip,
  SliceColorStripProps,
  SliceConnections,
  SliceConnectionsPanel,
  SliceConnectionsPanelProps,
  SliceConnectionsProps,
  SliceContactForm,
  SliceContactFormProps,
  SliceContent,
  SliceContentImage,
  SliceContentImageProps,
  SliceContentPanel,
  SliceContentPanelProps,
  SliceConversation,
  SliceConversationProps,
  SliceCuratorCardsGrid,
  SliceCuratorCardsGridProps,
  SliceDescription,
  SliceDescriptionProps,
  SliceEntityImagesCarousel,
  SliceEntityImagesCarouselProps,
  SliceEntityItems,
  SliceEntityItemsWithFilters,
  SliceEntityItemsWithFiltersProps,
  SliceEntityViewAsideMobile,
  SliceEntityViewAsideMobileProps,
  SliceHairline,
  SliceHeader,
  SliceHeaderProps,
  SliceImage,
  SliceIndicatorGroupsMain,
  SliceIntro,
  SliceIntroProps,
  SliceKeyMessage,
  SliceLatestInsights,
  SliceLatestInsightsProps,
  SliceLayoutComponentOptions,
  SlicePersonBioGrid,
  SlicePersonBioGridProps,
  SliceViewIntro,
  SliceViewIntroProps,
  usePageEntity,
  useSliceConnections,
} from '@shapeable/ui';
import { SliceExplorerChallengesLayout } from '../entities/slice-explorer-challenges-layout';
import { SliceAsideMapLink, SliceAsideMapLinkProps } from '../entities/slice-aside-map-link';
import { SliceChallengesNavigator } from '../entities/slice-challenges-navigator';
import { BRAND_COLORS, DOING_CHALK, NEUTRAL_BACKGROUND } from '../../theme';
import { ImageAsset } from '@shapeable/types';
import { SliceMailchimpEmbed, SliceMailchimpEmbedProps } from '../entities/slice-mailchimp-embed';
import { SliceMailchimpTownTeamEmbed, SliceMailchimpTownTeamEmbedProps } from '../entities/slice-mailchimp-town-team-embed';
import { SliceHumantixEmbed, SliceHumantixEmbedProps } from '../entities/slice-humantix-embed';
import { SliceKnowledgeHubLayout, SliceKnowledgeHubLayoutProps } from '../entities/slice-knowledge-hub-layout';
import { LinkCardTownTeam } from '../entities/link-card-town-team';
import { LinkCard } from '../entities/link-card';
import { SliceEditEntityButton, SliceEditEntityButtonProps } from '../entities/slice-edit-entity-button';
import { HumantixEmbedProps } from '../elements/humantix-embed';

export { SliceLayoutContactEmail } from './slice-layout-contact-email';

export const SliceLayoutMailchimpEmbed = createSliceLayout<SliceMailchimpEmbedProps>(
  SliceMailchimpEmbed, {
    boundary: 'none',
    verticalPadding: 'none',
  }
);

export const SliceLayoutMailchimpTownTeamEmbed = createSliceLayout<SliceMailchimpTownTeamEmbedProps>(
  SliceMailchimpTownTeamEmbed, {
    boundary: 'none',
    verticalPadding: 'none',
  }
);

export const SliceLayoutBentleyTogetherHumantixEmbed = createSliceLayout<HumantixEmbedProps>(
  SliceHumantixEmbed, {
    boundary: 'none',
    verticalPadding: 'none',
    props: {
      collection: 'bentley-together',
    }
  }
);

export const SliceLayoutHumantixEmbed = createSliceLayout<SliceHumantixEmbedProps>(
  SliceHumantixEmbed, {
    boundary: 'none',
    verticalPadding: 'none',
    props: {
      collection: 'town-team-movement-events',
    }
  }
);

// -------- Slice Layouts with Wide Content Links -------->

export const SliceLayoutContentImage = createSliceLayout<SliceContentImageProps>(
  SliceContentImage, {
    verticalPadding: 'small',
    props: {
      contentEntityBlockProps: {
        spacing: {
          titleSubtitle: 8,
          contentLinks: 9,
        },
      },
    },
  }
);

export const SliceLayoutContentImageReverse = createSliceLayout<SliceContentImageProps>(
  SliceContentImage, {
    verticalPadding: 'small',
    props: {
      reverse: true,
      contentEntityBlockProps: {
        spacing: {
          titleSubtitle: 8,
          contentLinks: 9,
        },
      },
    },
  }
);

export const SliceLayoutEntityViewAsideMobile = createSliceLayout<SliceEntityViewAsideMobileProps>(
  SliceEntityViewAsideMobile, {
    boundary: 'none',
    props: {
      dividerHeight: 150,
      expandedButtonOffset: 14,
    }
  }
);

export const SliceLayoutEntityViewAsideMobileRender = createSliceLayout<SliceEntityViewAsideMobileProps>(
  SliceEntityViewAsideMobile, {
    boundary: 'none',
    props: {
      dividerHeight: 150,
      expandedButtonOffset: 14,
      ignoreRenderCheck: true,
    }
  }
);

// -------- Slice Layouts with Tight Content Links -------->

const sliceLayoutEntityListOptions: SliceLayoutComponentOptions = {
  boundary: 'none',
  verticalPadding: 'panel',
  defaultBackgroundColor: { value: 'transparent' },
  useEntityColors: false,
  verticalBalanceDelta: 0,
  shouldRender: hasSliceContent,
  props: {
    showIcon: false,
    colors: {
      text: 'primary',
    }
  }
};

export const SliceLayoutLinkButton = createSliceLayout(
  SliceKeyMessage, {
    verticalPadding: 'none',
  }
);

export const SliceLayoutBannerTout = createSliceLayout<SliceBannerToutProps>(
  SliceBannerTout, {
    verticalPadding: 'medium',
    boundary: 'feature-slim',
    props: {
      aspectRatio: {
        base: 5,
        desktop: 4,
        desktopFullHD: 3
      }
    }
  }
);

export const SliceLayoutCuratorCardsGridDownload = createSliceLayout<SliceCuratorCardsGridProps>(
  SliceCuratorCardsGrid, {
    boundary: 'content',
    props: {
      card: LinkCardTownTeam
    }
  }
);

export const SliceLayoutCuratorCardsGrid = createSliceLayout<SliceCuratorCardsGridProps>(
  SliceCuratorCardsGrid, {
    boundary: 'content',
    props: {
      card: LinkCard
    }
  }
);

export const SliceLayoutEntityItems = createSliceLayout(
  SliceEntityItems, {
    boundary: 'none',
    verticalPadding: 'small'
  }
);

export const SliceLayoutEntityItemsWithFiltersAndSearch = createSliceLayout<SliceEntityItemsWithFiltersProps>(
  SliceEntityItemsWithFilters, {
    boundary: 'padding',
    verticalPadding: 'none'
  }
);

export const SliceLayoutEntityItemsWithFilters = createSliceLayout<SliceEntityItemsWithFiltersProps>(
  SliceEntityItemsWithFilters, {
    boundary: 'padding',
    verticalPadding: 'none',
    props: {
      showSearchBar: false,
    }
  }
);

export const SliceLayoutPeople = createSliceLayout<SlicePersonBioGridProps>(
  SlicePersonBioGrid, {
    verticalPadding: 'small',
    boundary: 'content-bleed',
    props: {
      transform: 'grayscale',
    }
  }
);

export const SliceLayoutExplorerChallengesLayout = createSliceLayout(
  SliceExplorerChallengesLayout, {
    boundary: 'none',
    verticalPadding: 'none'
  }
);

export const SliceLayoutHairline = createSliceLayout(
  SliceHairline, {
    boundary: 'none',
  }
);

export const SliceLayoutViewIntro = createSliceLayout<SliceViewIntroProps>(
  SliceViewIntro, {
    boundary: 'none',
    verticalPadding: 'none',
    props: {
      fontWeight: '400',
    }
  }
);

export const SliceLayoutDescription = createSliceLayout<SliceDescriptionProps>(
  SliceDescription, {
    boundary: 'none',
    verticalPadding: 'panel',
    verticalBalanceDelta: 0,
    shouldRender: (entity) => !!entity?.description?.text || !!entity?.content?.text,
    props: {
      fontWeight: '300',
    }
  }
);

export const SliceLayoutContentPanel = createSliceLayout<SliceContentPanelProps>(
  SliceContentPanel, sliceLayoutEntityListOptions
);

const SliceLayoutConnectionsPanelImpl = createSliceLayout<SliceConnectionsPanelProps>(
  SliceConnectionsPanel, {
    boundary: 'none',
    verticalPadding: 'panel',
    defaultBackgroundColor: { value: 'transparent' },
    useEntityColors: false,
    verticalBalanceDelta: 0,
    props: {
      panelProps: {
        showIcon: false,
        colors: {
          text: 'primary',
        }
      },
    }
  }
);

export const SliceLayoutConnectionsPanel = (sliceLayoutProps: SliceConnectionsPanelProps) => {
  const defn = useSliceConnections(sliceLayoutProps.entity);
  if (!defn.shouldRender) {
    return null;
  }
  return SliceLayoutConnectionsPanelImpl(sliceLayoutProps);
};

const SliceLayoutConnectionsPanelIconImpl = createSliceLayout<SliceConnectionsPanelProps>(
  SliceConnectionsPanel, {
    boundary: 'none',
    verticalPadding: 'panel',
    defaultBackgroundColor: { value: 'transparent' },
    useEntityColors: false,
    verticalBalanceDelta: 0,
    props: {
      panelProps: {
        showIcon: true,
        colors: {
          text: 'primary',
        }
      },
    }
  }
);

export const SliceLayoutConnectionsPanelIcon = (sliceLayoutProps: SliceConnectionsPanelProps) => {
  const defn = useSliceConnections(sliceLayoutProps.entity);
  if (!defn.shouldRender) {
    return null;
  }
  return SliceLayoutConnectionsPanelIconImpl(sliceLayoutProps);
};

const sliceLayoutConnectionsImpl = createSliceLayout(
  SliceConnections, {
    boundary: 'none',
    verticalPadding: 'panel',
    defaultBackgroundColor: { value: 'transparent' },
    useEntityColors: false,
    verticalBalanceDelta: 0,
  }
);

export const SliceLayoutConnections = (sliceLayoutProps: SliceConnectionsProps) => {
  const defn = useSliceConnections(sliceLayoutProps.entity);
  if (!defn.shouldRender) {
    return null;
  }
  return sliceLayoutConnectionsImpl(sliceLayoutProps);
};

export const SliceLayoutAsideMapLink = createSliceLayout<SliceAsideMapLinkProps>(
  SliceAsideMapLink, {
    verticalPadding: 'none',
    boundary: 'none',
    props: {
      zoom: 16,
    }
  }
);

export const SliceLayoutEditEntityButton = createSliceLayout<SliceEditEntityButtonProps>(
  SliceEditEntityButton, {
    boundary: 'none',
    verticalPadding: 'none',
  }
);

export const SliceLayoutChallengesNavigator = createSliceLayout(
  SliceChallengesNavigator, {
    boundary: 'none',
    verticalPadding: 'none',
  }
);

export const SliceLayoutIndicatorGroupsMain = createSliceLayout(
  SliceIndicatorGroupsMain, {
    boundary: 'feature',
    verticalPadding: 'none',
  }
);

export const SliceLayoutColorStrip = createSliceLayout<SliceColorStripProps>(
  SliceColorStrip, {
    boundary: 'none',
    props: {
      colors: BRAND_COLORS
    }
  }
);

export const SliceLayoutContactForm = createSliceLayout<SliceContactFormProps>(
  SliceContactForm, {
    props: {
      variant: 'inline',
      showMembershipCheckbox: false,
      showSubscribeCheckbox: false,
    }
  }
);

export const SliceLayoutConversation = createSliceLayout<SliceConversationProps>(
  SliceConversation, {
    boundary: 'none',
    verticalPadding: 'none',
    props: {
      showBanner: false,
    }
  }
);

export const SliceLayoutIntroLeft = createSliceLayout<SliceIntroProps>(
  SliceIntro, {
    verticalPadding: 'slim',
    props: {
      align: 'left',
    }
  }
);

export const SliceLayoutIntro = createSliceLayout<SliceIntroProps>(
  SliceIntro, {
    verticalPadding: 'small',
    props: {
      align: 'left',
    }
  }
);

export const SliceLayoutImageSingle = createSliceLayout(
  SliceImage, {
    boundary: 'content-single',
    useBackgroundImages: false,
  }
);

export const SliceLayoutHeader = createSliceLayout<SliceHeaderProps>(
  SliceHeader, {
    verticalPadding: 'none',
    props: {
      alignLeft: true,
    }
  }
);

export const SliceLayoutContent = createSliceLayout(
  SliceContent, {
    verticalPadding: 'none',
  }
);

export const SliceLayoutKnowledgeHub = createSliceLayout<SliceKnowledgeHubLayoutProps>(
  SliceKnowledgeHubLayout, {
    boundary: 'content',
    verticalPadding: 'medium',
    props: {
      showAllTab: false,
      filterSize: 'slim',
      filterSpacing: 5,
      useContentTabs: true,
      contentTabsProps: { colors: { background: DOING_CHALK, text: 'dark' } },
      entityTypes: { 
        Entity: {
          label: 'Reads',
          latestTitle: 'Latest Reads',
          sortBy: 'published',
          reverse: true,
          filterFieldNames: ['types'],
          spacing: 3,
          // toggleTypes: [
          //   { name: 'Post', label: 'Posts' },
          //   { name: 'FeedEntry', label: 'Articles' },
          //   { name: 'Video', label: 'Videos' },
          // ],
        },
      },
    }
  }
);

const SliceLayoutConnectionsPanelCount3Impl = createSliceLayout<SliceConnectionsPanelProps>(
  SliceConnectionsPanel, {
    boundary: 'none',
    verticalPadding: 'panel',
    defaultBackgroundColor: { value: 'transparent' },
    useEntityColors: false,
    verticalBalanceDelta: 0,
    props: {
      itemsCount: 3,
    }
  }
);

export const SliceLayoutConnectionsPanelCount3 = (sliceLayoutProps: SliceConnectionsPanelProps) => {
  const defn = useSliceConnections(sliceLayoutProps.entity);
  if (!defn.shouldRender) {
    return null;
  }
  return SliceLayoutConnectionsPanelCount3Impl(sliceLayoutProps);
};

const sliceLayoutImagesCarouselImpl = createSliceLayout<SliceEntityImagesCarouselProps>(
  SliceEntityImagesCarousel,
  {
    verticalPadding: 'small',
    boundary: 'none',
    props: {
      heroVariant: 'contain-blur',
    },
  }
);

export const SliceLayoutImagesCarousel = (sliceLayoutProps: SliceEntityImagesCarouselProps) => {
  const { entity, items } = sliceLayoutProps;

  const activeEntity = usePageEntity() as { imageAssets?: ImageAsset[] };
  const { backgroundImages = [], images = [], imageAssets = [] } = entity || {};

  const itemsToUse = !!items?.length
    ? items
    : !!backgroundImages.length
      ? backgroundImages
      : !!images.length
        ? images
        : imageAssets;

  const activeEntityItems = activeEntity?.imageAssets || [];
  const resolvedItems = activeEntityItems.length ? activeEntityItems : itemsToUse;

  if (!resolvedItems.length) {
    return null;
  }

  return sliceLayoutImagesCarouselImpl(sliceLayoutProps);
};

export const SliceLayoutLatestInsights = createSliceLayout<SliceLatestInsightsProps>(
  SliceLatestInsights, {
    boundary: 'content-bleed',
    verticalPadding: 'small',
    props: {
      spacing: 2,
    }
  }
);